<template>
	<div class="calendar d-flex flex-column h-100 position-relative">
		<div style="height: 65px">
			<navigator @updateArrowDate="calculateSunTimes" :calendarApi="calendarApi"
				:fullCalendarView="fullCalendarView">
			</navigator>
		</div>

		<div class="calendar-content-p d-flex flexGrow1 position-relative">
			<div class="flexGrow1">
				<div class="calendar-wrapper" ref="calendarWidth" @mousemove="positionCursorX"
					:class="{ 'hide-day-header': getCurrentView === 'agendaDay' }">
					<span class="config-gear"><i class="fas fa-cog ml-2 pointer dark fs-large"
							@click.stop="openSettings"></i></span>

					<div class="calendar-loading-overlay" v-if="isLoadingCalendarEvents">
						<loader></loader>
					</div>

					<FullCalendar ref="fullCalendar" :options="calendarOptions" />
				</div>
			</div>

			<events @changeFilters="filtersChanged" :calendarApi="calendarApi"></events>
		</div>

		<div style="min-height: 1rem;"></div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { eventHub } from "@/main"

import debounce from 'lodash/debounce'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'

import Navigator from "./Navigator"
import Events from "./Events"
import Filters from "./Filters"
import Loader from '@/tools/loaders/loader1'
import { getEventEditChannel } from '@/utils/eventEditChannel'
import { showLoadingEventToast, hideLoadingEventToast } from '@/utils/loadingEventToast'
import vrBadgeIcon from '@/assets/images/badges/badge_vr_dark.svg'

const VIEW_NAME_MAP = {
	agendaDay: 'timeGridDay',
	agendaWeek: 'timeGridWeek',
	month: 'dayGridMonth',
};
const VIEW_NAME_MAP_REVERSE = {
	timeGridDay: 'agendaDay',
	timeGridWeek: 'agendaWeek',
	dayGridMonth: 'month',
};

export default {
	data() {
		return {
			calendarApi: null,
			isLoadingCalendarEvents: false,
			defaultView: "agendaDay",
			start: null,
			end: null,
			loading: true,
			loaded: false,
			cancelEventsRequestToken: null,
			choosedChannel: null,
			targetEventID: null,
			isCursorLeft: null,
			selectedDate: new Date().toISOString().substring(0, 10),
			lastWindow: null,
			pendingFetch: null,
			onUpdateCalendar: null,
			resizeObserver: null,
			resizeRAF: null,
		};
	},
	beforeDestroy() {
		eventHub.$off('updateCalendar', this.onUpdateCalendar);

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}

		if (this.resizeRAF) {
			cancelAnimationFrame(this.resizeRAF);
			this.resizeRAF = null;
		}
	},
	beforeMount() {
		this.calculateSunTimes();
	},
	created() {
		let currentView = this.$route.params.currentView || null;
		let expandedDay = this.$route.params.expandedDay || null;
		let expandedDate = this.nowDate();

		if (expandedDay) {
			expandedDate = expandedDay;
		}

		if (currentView) {
			this.updateCurrentView(currentView);

			if (currentView === "month") {
				this.defaultView = "month";
			} else {
				this.defaultView =
					"agenda" + (currentView.charAt(0).toUpperCase() + currentView.slice(1));
			}
		} else {
			if (this.getCurrentAccountType) {
				const defaultViewOptions = {
					0: 'agendaDay',
					1: 'agendaWeek',
					2: 'month'
				}

				this.defaultView = defaultViewOptions[this.getCurrentAccountType.defaultCalendarView];
			} else {
				this.updateCurrentView(this.defaultView.split("agenda").pop().toLowerCase());
			}
		}

		this.updateExpandedDate(expandedDate);

		this.onUpdateCalendar = () => {
			if (!this.calendarApi) return;

			this.flushEvents();
			this.calendarApi.refetchEvents();
		}
	},
	mounted() {
		this.calendarApi = this.$refs.fullCalendar.getApi();
		this.loading = false;
		this.loaded = true;

		this.calendarApi.gotoDate(this.getExpandedDay);
		this.calculateSunTimes();

		eventHub.$on('updateCalendar', this.onUpdateCalendar);
		
		if (this.$refs.calendarWidth && typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(() => {
				if (this.resizeRAF) return;

				this.resizeRAF = requestAnimationFrame(() => {
					this.resizeRAF = null;
					if (this.calendarApi) this.calendarApi.updateSize();
				});
			});

			this.resizeObserver.observe(this.$refs.calendarWidth);
		}

		this.channel = getEventEditChannel();
		if (!this.channel) return;

		this.channel.onmessage = (event) => {
			const payload = event.data ? event.data : null;
			if (!payload || !payload.event || !payload.event.id) return;

			const isEditModalOpen = this.$route.name === 'calendarExpandedWithEventIdEdit';
			if (isEditModalOpen) return;

			const isSameSelected = this.getSelectedEvent.id === payload.event.id;
			const isEventDetailsSideBarOpen = this.$route.name === 'calendarExpandedWithEventId';

			if (isSameSelected && isEventDetailsSideBarOpen) {
				this.updateSelectedEvent(payload.event.id);
				this.downloadGenderPricePerChannel(payload.event.id);
				this.initEventNotes(payload.event.id);
			}

			if (!this.hasEventSameDate(payload.event)) return;

			this.flushEvents();
			if (!this.calendarApi) return;
			this.calendarApi.refetchEvents();
		};
	},
	watch: {
		'$route.params.expandedDay': {
			immediate: true,
			handler(newDay) {
				if (newDay && this.calendarApi) {
					this.updateExpandedDate(newDay)
					this.calendarApi.gotoDate(newDay)
				}
			}
		},
		'getCompanySettings.calendarTimeStart'(newVal, oldVal) {
			if (!this.calendarApi) return;
			if (newVal === oldVal || oldVal === undefined) return;

			this.calendarApi.setOption('scrollTime', newVal || '08:00:00');
			this.calculateSunTimes();
			this.calendarApi.refetchEvents();
		},
		'getCompanySettings.isToShowSunriseAndSunsetOnCalendar'(newVal, oldVal) {
			if (!this.calendarApi) return;
			if (newVal === oldVal || oldVal === undefined) return;

			this.calculateSunTimes();
			this.calendarApi.refetchEvents();
		},
		'$route': {
			immediate: true,
			handler(route) {
				const newEventId = route.params.eventID;
				if (!this.$route.name || !newEventId) return;

				if (route.name === 'calendarExpandedWithEventIdEdit') {
					this.simulateEventSelection(newEventId);
					this.openModal({ name: 'EventEdit', props: { selectedEvent: this.getSelectedEvent } });
					return;
				}

				this.closeModal();
				this.simulateEventSelection(newEventId);
			}
		},
	},
	computed: {
		...mapGetters([
			"getExpandedDay",
			"getCurrentView",
			"getFilters",
			"getEvents",
			"account",
			"getSelectedEvent",
			"getCompanySettings",
			"getStoredEvents",
			"getEventColors",
			"getCurrentAccountType",
			"getSalesChannels"
		]),
		salesChannel() {
			let output = this.getSalesChannels.filter((c) => c.isImmutable);
			output.unshift({ brandName: "None Selected" });

			return output;
		},
		isToShowStartEndTime() {
			return this.getCurrentAccountType.isCalendarToShowStartEndTime;
		},
		isToShowProductName() {
			return this.getCurrentAccountType.isCalendarToShowProductName;
		},
		isToShowEventResourcesName() {
			return this.getCurrentAccountType.isCalendarToShowEventResourcesName;
		},
		isToShowQuantityAvailable() {
			return this.getCurrentAccountType.isCalendarToShowQuantityAvailable;
		},
		isToShowOccupancy() {
			return this.getCurrentAccountType.isCalendarToShowOccupancy;
		},
		fullCalendarView() {
			return this.calendarApi ? this.calendarApi.view : null;
		},

		calendarOptions() {
			const vm = this;

			return {
				plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
				locale: "en",
				firstDay: 1,
				allDaySlot: false,
				navLinks: true,
				initialView: VIEW_NAME_MAP[vm.defaultView] || 'timeGridDay',
				slotEventOverlap: false,
				eventOverlap: false,
				nowIndicator: true,
				slotMinTime: "00:00:00",
				slotMaxTime: "24:00:00",
				scrollTime: this.getCompanySettings.calendarTimeStart
					? this.getCompanySettings.calendarTimeStart
					: "08:00:00",
				height: "100%",
				defaultTimedEventDuration: "01:00:00",
				eventTimeFormat: { hour: 'numeric', minute: '2-digit', hour12: false },
				slotLabelFormat: { hour: 'numeric', minute: '2-digit', hour12: false },
				headerToolbar: false,
				selectable: false,
				editable: false,

				views: {
					dayGridMonth: { dayHeaderFormat: { weekday: 'short' } },
					timeGridWeek: { dayHeaderFormat: { weekday: 'short', day: 'numeric' } },
					timeGridDay: { dayHeaderFormat: { weekday: 'long', day: 'numeric', month: 'numeric' } },
				},
				datesSet(dateInfo) {
					vm.updateCurrentView(VIEW_NAME_MAP_REVERSE[dateInfo.view.type] || dateInfo.view.type);
					vm.calculateSunTimes();
					vm.fetchCalendarEvents(dateInfo);
				},
				eventContent(arg) {
					return vm.buildEventContent(arg);
				},
				eventDidMount(info) {
					vm.decorateEvent(info);
				},
				eventsSet(events) {
					requestAnimationFrame(() => {
						vm.addSpecialHoverMove();
						eventHub.$emit('eventsRendered');
					});
				},

				eventDrop(info) {
					vm.pushEvent(info.event);
				},
				eventResize(info) {
					vm.pushEvent(info.event);
				},

				eventClick(clickInfo) {
					const ext = clickInfo.event.extendedProps || {};

					if (ext.eventType === 'sunrise' || ext.eventType === 'sunset') {
						return;
					}
					
					const rawId = clickInfo.event.id;
					const storeEventId = (rawId !== '' && !isNaN(rawId)) ? Number(rawId) : rawId;

					vm.targetEventID = rawId;
					vm.updateSelectedEvent(storeEventId);
					vm.downloadGenderPricePerChannel(storeEventId);

					const viewMap = { timeGridDay: 'day', timeGridWeek: 'week', dayGridMonth: 'month' };
					const viewNameForUrl = viewMap[vm.calendarApi.view.type];

					if (vm.$router.history.current.name !== 'calendarExpandedWithEventIdEdit') {
						vm.$router.push({
							name: 'calendarExpandedWithEventId',
							params: {
								currentView: viewNameForUrl,
								expandedDay: vm.getExpandedDay,
								eventID: rawId,
							},
						});
					}

					document.querySelectorAll('.fc-event.selected').forEach(el => el.classList.remove('selected'));
					clickInfo.el.classList.add('selected');

					const eventsPanel = document.querySelector('.events');
					if (eventsPanel) {
						eventsPanel.classList.add('showingMore', 'overflow-y-auto');
						const listEl = eventsPanel.querySelector('.list');
						const moreEl = eventsPanel.querySelector('.more');
						if (listEl) listEl.classList.add('moved-left');
						if (moreEl) moreEl.classList.remove('moved-right');
					}
					
					setTimeout(() => {
						if (vm.calendarApi) vm.calendarApi.updateSize();
					}, 250);
				},

				navLinkDayClick() {
					return false;
				},

				select(selectionInfo) {
					vm.start = selectionInfo.start;
					vm.end = selectionInfo.end;
				},
			};
		},
	},
	methods: {
		...mapActions([
			"commitEvents",
			"updateCurrentView",
			"updateExpandedDate",
			"updateSidebar",
			"pushEvent",
			"updateSelectedEvent",
			"openModal",
			"refreshAccountData",
			"initCompany",
			"downloadEvents",
			"prepareEventDates",
			"downloadEventColors",
			"flushEvents",
			"downloadSingleEvent",
			"closeModal",
			"calculateSunTimes",
			'downloadGenderPricePerChannel',
			'initEventNotes',
		]),
		fetchCalendarEvents(fetchInfo) {
			const startKey = moment(fetchInfo.start).format('YYYY-MM-DD');
			const endKey = moment(fetchInfo.end).format('YYYY-MM-DD');

			if (this.pendingFetch && this.pendingFetch.start === startKey && this.pendingFetch.end === endKey) {
				return;
			}

			const isNewWindow = !this.lastWindow || this.lastWindow.start !== startKey || this.lastWindow.end !== endKey;

			if (isNewWindow) {
				this.flushEvents();
				this.lastWindow = { start: startKey, end: endKey };
			}

			this.isLoadingCalendarEvents = true;

			const promise = this.downloadEvents({
				supplierId: this.account.supplierId,
				dateFrom: startKey,
				dateTo: endKey
			});

			this.pendingFetch = { start: startKey, end: endKey, promise };

			promise.catch((err) => {
				if (axios.isCancel(err)) return;
				console.error(err);
			}).finally(() => {
				this.isLoadingCalendarEvents = false;
				if (this.pendingFetch && this.pendingFetch.promise === promise) {
					this.pendingFetch = null;
				}
			});
		},
		hexToRgba(hex, alpha) {
			if (!hex) return hex;

			let normalized = hex.replace('#', '');
			if (normalized.length === 3) {
				normalized = normalized.split('').map(c => c + c).join('');
			}

			const r = parseInt(normalized.substring(0, 2), 16);
			const g = parseInt(normalized.substring(2, 4), 16);
			const b = parseInt(normalized.substring(4, 6), 16);

			if (isNaN(r) || isNaN(g) || isNaN(b)) return hex;

			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		},

		hasEventSameDate(event) {
			if (!this.calendarApi || !event.start) return true;

			const view = this.calendarApi.view;
			if (!view || !view.activeStart || !view.activeEnd) return true;

			const eventStart = new Date(event.start);
			return eventStart >= view.activeStart && eventStart < view.activeEnd;
		},
		filtersChanged() {
			eventHub.$emit('updateCalendar');
		},
		simulateEventSelection(eventId) {
			const vm = this;
			const elementId = `refid-${eventId}`;
			let timeoutId;

			const currentSelectedId = (vm.getSelectedEvent && vm.getSelectedEvent.id) || null;
			if (currentSelectedId === eventId) {
				vm.targetEventID = eventId;
				return;
			}

			vm.targetEventID = eventId;

			vm.$nextTick(() => {
				const el = document.getElementById(elementId);

				if (el) {
					clearTimeout(timeoutId);
					el.click();
					return;
				}

				showLoadingEventToast(vm, { duration: 10000 });

				const onRendered = () => {
					const e2 = document.getElementById(elementId);

					if (e2) {
						clearTimeout(timeoutId);
						eventHub.$off('eventsRendered', onRendered);
						e2.click();
						hideLoadingEventToast();
					}
				};

				eventHub.$on('eventsRendered', onRendered);

				timeoutId = setTimeout(() => {
					eventHub.$off('eventsRendered', onRendered);
					hideLoadingEventToast();
					vm.$toasted.error('Event not found! Please update your filters and try again', { duration: 3000 });
				}, 10000);
			});
		},
		positionCursorX(e) {
			const posX = e.clientX;
			const halfCalendarWidth = this.$refs.calendarWidth.clientWidth / 2;
			this.isCursorLeft = posX < halfCalendarWidth;
		},
		openSettings() {
			this.openModal("calendarSettings");
		},
		buildEventContent(arg) {
			const vm = this;
			const event = arg.event;
			const ext = event.extendedProps || {};
			
			if (ext.eventType === 'sunrise' || ext.eventType === 'sunset') {
				if (arg.view.type === 'dayGridMonth') {
					return { domNodes: [] };
				}

				const icon = document.createElement('i');
				icon.className = ext.eventType === 'sunrise' ? 'icon-sunrise large tooltip' : 'icon-sunset large tooltip';
				icon.title = `${event.title}: ${moment(event.start).format('HH:mm')}`;
				return { domNodes: [icon] };
			}

			if (ext.hideOnCalendar === true) {
				return { domNodes: [] };
			}

			const wrapper = document.createElement('div');
			wrapper.className = 'fc-content';

			const colFirst = document.createElement('div');
			colFirst.className = 'fc-column-first';

			const colSecond = document.createElement('div');
			colSecond.className = 'fc-column-second';
			
			if (event.id !== undefined && ext.product !== undefined) {
				if (ext.availabilityType == 2 && ext.shareType == 1) {
					const sharedIcon = document.createElement('div');
					sharedIcon.className = 'sharedEventIcon';
					sharedIcon.innerHTML = '<img src="/static/images/event/shared_product_icon_black.svg">';
					wrapper.appendChild(sharedIcon);
				}

				if (vm.isToShowProductName) {
					const prodEl = document.createElement('div');
					prodEl.className = 'd-block fc-title fc-content--product-name';
					
					if (ext.shareType === 2) {
						const vrBadge = document.createElement('img');
						vrBadge.src = vrBadgeIcon;
						vrBadge.className = 'vr-badge-inline mr-1';
						prodEl.appendChild(vrBadge);
					}

					const b = document.createElement('b');
					let outputName = ext.nameAlias ? ext.nameAlias : ext.product.name;
					if (ext.status == -1) outputName += " (Canceled)";
					b.textContent = outputName;
					prodEl.appendChild(b);
					colFirst.appendChild(prodEl);
				}
			}

			// Time label
			if (vm.isToShowStartEndTime) {
				const timeEl = document.createElement('div');
				timeEl.className = 'fc-time';

				if (ext.originalStart && ext.originalEnd) {
					const formatHM = (d) => String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
					timeEl.textContent = `${formatHM(new Date(ext.originalStart))} - ${formatHM(new Date(ext.originalEnd))}`;
				} else if (arg.timeText) {
					timeEl.textContent = arg.timeText;
				}

				colFirst.appendChild(timeEl);
			}

			if (event.id !== undefined && ext.product !== undefined) {
				let availability = null;
				if (ext.availabilityType == 1) {
					availability = "Unlimited";
				} else {
					availability = ext.quantityAvailable;
				}

				const resourceOnEvent = (ext.eventResources || []).map(r => r.name);

				if (vm.isToShowQuantityAvailable) {
					const availEl = document.createElement('div');
					availEl.className = 'd-block fc-title fc-content--available-seats';
					let text = 'Available: ' + availability;
					if (ext.allotted !== undefined && ext.allotted > 0) {
						text += ` (${ext.allotted} allotted)`;
					}
					availEl.textContent = text;
					colFirst.appendChild(availEl);
				}

				if (vm.isToShowEventResourcesName && resourceOnEvent.length > 0) {
					const resEl = document.createElement('div');
					resEl.className = 'd-block fc-title fc-content--resource';
					resEl.textContent = resourceOnEvent.join(', ');
					colFirst.appendChild(resEl);
				}

				// Channel availability dot
				const choosedChannel = vm.salesChannel.find(
					(c) => c.id == vm.getCurrentAccountType.selectedSalesChannelToShowInIndicatorID
				);
				if (choosedChannel && choosedChannel.name && (ext.cutOffStatus === 1 || ext.cutOffStatus == 2)) {
					const isOpen = ext.cutOffStatus === 1;
					const dotWrap = document.createElement('div');
					dotWrap.className = 'channelAvailability';
					const dot = document.createElement('span');
					dot.title = `Sales Channel ( ${choosedChannel.name} ) is ${isOpen ? 'OPEN' : 'CLOSED'}`;
					dot.className = `channelCircle tooltip mr-1 radius-circle bg-medium ${isOpen ? 'bg-color-3' : 'bg-color-6'}`;
					dotWrap.appendChild(dot);
					colSecond.appendChild(dotWrap);
				}

				// Occupancy rate
				if (vm.isToShowOccupancy) {
					const occEl = document.createElement('div');
					occEl.className = 'occupancy-rate';

					if (!ext.hasLotation) {
						occEl.style.cssText = 'margin-bottom:-5px;font-size:19px;color:#6c6b6b;';
						occEl.innerHTML = '<span title="This event has unlimited capacity.">&#8734;</span>';
					} else {
						const occupancy = ext.maximumLotation == 0 ? '-' : `${Math.round((ext.quantityBooked * 100) / ext.maximumLotation)}%`;
						occEl.innerHTML = `<span title="Occupancy rate for this event is ${occupancy}">${occupancy}</span>`;
					}

					colSecond.appendChild(occEl);
				}
			}

			wrapper.appendChild(colFirst);
			wrapper.appendChild(colSecond);

			return { domNodes: [wrapper] };
		},

		decorateEvent(info) {
			const vm = this;
			const event = info.event;
			const ext = event.extendedProps || {};
			const element = info.el;
			const defaultColor = "bg-color-1";

			if (event.id === vm.targetEventID) {
				element.classList.add('selected');
			}

			if (ext.hideOnCalendar === true) {
				element.classList.remove(defaultColor);
				return;
			}

			if (ext.eventType === 'sunrise' || ext.eventType === 'sunset') {
				element.classList.remove(defaultColor);
				element.classList.add('event-sun-line', 'm-0', 'p-0');
				if (info.view.type === 'dayGridMonth') {
					element.style.display = 'none';
					return;
				}

				const harness = element.closest('.fc-timegrid-event-harness');
				if (harness) {
					harness.style.left = '0';
					harness.style.right = '0';
					harness.style.width = '100%';
					harness.style.zIndex = 5;
				}
				return;
			}

			const now = moment().add(moment().utcOffset(), "minutes");
			
			if (ext.product !== undefined) {
				const nameForTooltip = ext.nameAlias ? ext.nameAlias : ext.product.name;
				const timeForTooltip = event.start ? moment(event.start).format('HH:mm') + (event.end ? ' - ' + moment(event.end).format('HH:mm') : '') : '';
				const availabilityForTooltip = ext.availabilityType == 1 ? 'Unlimited' : ext.quantityAvailable;
				element.title = `${nameForTooltip}\n${timeForTooltip}\nAvailable: ${availabilityForTooltip}`;
			}

			if ((!event.end && moment(event.start).isBefore(now)) || moment(event.end).isBefore(now)) {
				element.classList.add("past");
			}
			if (ext.status == -1) element.classList.add("eventCanceled");
			if (ext.status == -2) element.classList.add("eventDisabled");
			if (ext.quantityBooked > 0) element.classList.add("hasBookings");
			if (ext.shareType === 2) element.classList.add("virtualRooster");

			element.id = "refid-" + event.id;

			if (ext.product !== undefined && ext.product.color !== undefined) {
				const color = vm.getEventColors.filter(c => ext.product.color == c.id);
				if (color && color.length > 0) {
					element.style.backgroundColor = vm.hexToRgba(color[0].code, 0.85);
					element.style.borderColor = color[0].codeSecundary;
					element.style.borderWidth = '1px';
				}
				element.classList.add('radius-normal');
			} else {
				element.classList.add(defaultColor);
			}

			vm.attachHoverExpand(element);
		},
		attachHoverExpand(element) {
			const vm = this;

			const elementWidthIsSmallerThenPresetWidth = () => {
				const presetWidth = 91;
				return element.getBoundingClientRect().width < presetWidth;
			};

			const contentEl = element.querySelector('.fc-content');
			const targetElementToExpand = contentEl ? contentEl.parentElement : null;
			if (!targetElementToExpand) return;

			element.addEventListener('mouseenter', () => {
				if (elementWidthIsSmallerThenPresetWidth()) {
					targetElementToExpand.classList.add(vm.isCursorLeft ? 'expand-width-right' : 'expand-width-left');
				}
			});

			element.addEventListener('mouseleave', () => {
				targetElementToExpand.classList.remove('expand-width-left', 'expand-width-right');
			});
		},
		
		addSpecialHoverMove() {
			const events = Array.from(document.querySelectorAll('.fc-event'));

			for (const el of events) {
				const rect = el.getBoundingClientRect();
				if (rect.top > 800 && !el.dataset.hoverScrollBound) {
					el.dataset.hoverScrollBound = 'true';
					el.addEventListener('mouseover', (e) => {
						e.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
					});
				}
			}
		},
	},
	components: {
		FullCalendar,
		Navigator,
		Events,
		Filters,
		Loader,
	},
};
</script>

<style lang="scss">
@import "../../assets/scss/_variables.scss";

.channelCircle {
	width: 8px;
	height: 8px;
	display: inline-block;
}

.calendar-wrapper {
	height: 100%;
	touch-action: pan-y;
	position: relative;
}

.calendar-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 30;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba($lightest, 0.65);
}

.fc {
	--fc-event-text-color: #{$darkest};

	.fc-now-indicator {
		border: 1px solid rgba($brand, 0.6);
	}

	.fc-timegrid-slot {
		height: 2.75rem;
	}

	thead {
		border: 0;

		td {
			border: 0;
			font-size: $font-size-small;
			padding: 0.9rem 0;
		}
	}

	th {
		border: 0;

		&.fc-day-header,
		&.fc-col-header-cell {
			&.fc-day-today {
				>a {
					color: $lightest;
					background-color: $brand;
					border-radius: 10rem;
					padding: 0.4rem 0.75rem;
				}
			}
		}
	}

	td {
		border: 1px solid $light;

		&.fc-day-today,
		&.fc-day-past,
		&.fc-day-future {
			background: transparent;
		}
	}

	.fc-event {
		border-color: $medium;
		border-radius: 0;
		margin: 0 0.3%;
		padding: 1%;
		color: $darkest;
		cursor: pointer;
		user-select: none;

		&.eventCanceled {
			border-color: $color-6;
			border-width: 2px;
			border-style: solid;

			background: repeating-linear-gradient(45deg,
					$color-6,
					$color-6 10px,
					lighten($color-6, 5%) 10px,
					lighten($color-6, 5%) 20px);
		}

		&.eventDisabled {
			background-color: $light !important;
			color: $dark !important;
		}

		&.selected {
			border: 1px solid $lightest;
			outline: 1px dashed $dark;
		}

		.sharedEventIcon {
			position: absolute;
			right: 0;
			top: 0;
		}
	}

	.fc-view-harness {
		background-color: $lightest;
	}

	.fc-timegrid-slot-label {
		text-transform: uppercase;
		font-size: $font-size-small;
	}

	.fc-timegrid-event .fc-event-time,
	.fc-timegrid-event .fc-event-title {
		font-size: 0.7rem;
		margin-bottom: -2px;
	}

	.fc-daygrid-event {
		margin: 1% 0;
		padding: 0.5% 1%;
	}
}

.fc-event {
	&.virtualRooster {
		opacity: 0.8;
		
		&.hasBookings {
			opacity: 1;
		}
	}
}

#calendar,
.calendar-wrapper {
	.fc-scroller {
		scrollbar-width: thin; // Firefox

		&::-webkit-scrollbar {
			width: 8px; // Chrome/Edge/Safari
		}
	}

	.fc-timegrid-event-harness:hover {
		z-index: 1000 !important;
	}
	
	&.hide-day-header .fc-col-header {
		display: none;
	}
}

.vr-badge-inline {
	height: 14px;
	width: 14px;
	vertical-align: middle;
	display: inline-block;
}

.fc-content {
	height: 100%;
	width: 100%;
	display: flex;
	position: relative;
	flex-direction: row;
	overflow: hidden;

	.fc-title {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	.channelAvailability {
		position: absolute;
		z-index: 100000;
		bottom: 4px;
		right: -3px;
	}

	.occupancy-rate {
		font-size: $font-size-small;
		position: absolute;
		z-index: 100000;
		bottom: 0;
		right: 13px;
	}

	.fc-column-second {
		display: flex;
		flex-direction: column;
		justify-content: end;
	}

	.fc-column-first {
		flex-direction: column;
		margin-bottom: 18px;
		min-width: 0;
	}
}

.config-gear {
	position: absolute;
	right: 0.75rem;
	top: 0.5rem;
	z-index: 20;
	cursor: pointer;
	color: #75859a;
}

.expand-on-hover {
	z-index: 100 !important;
	bottom: auto !important;
}

.expand-width-left {
	left: auto !important;
	z-index: 99 !important;
	min-width: 7.5rem;
}

.expand-width-right {
	right: auto !important;
	z-index: 99 !important;
	min-width: 7.5rem;
}

.fc-event.event-sun-line {
	border-top: 1px solid orange;
	border-left: 0;
	border-right: 0;
	border-bottom: 0;
	height: 0px !important;
	background: transparent !important;
}
</style>


// WEBPACK FOOTER //
// Calendar.vue?ee892e80